"use client";

import { useState } from "react";
import { FiExternalLink, FiCalendar, FiUser, FiLink2, FiClock, FiShield } from "react-icons/fi";
import type { EventData } from "@/hooks/useEventQuery";
import type { Source, Market } from "./types";
import { GavelIcon } from "@/app/components/icons/GavelIcon";
import { useT } from "@/hooks/useT";

interface ResolutionDetailsProps {
  source: Source;
  eventData: EventData;
  selectedMarketDetails: any;
  selectedMarket: Market | null;
}

function extractDomain(url: string): string {
  try {
    const { hostname } = new URL(url);
    return hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
}

/** Small, consistent metadata card. Label row + value (with optional link affordance). */
function MetaCard({
  icon,
  label,
  value,
  href,
}: {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  href?: string;
}) {
  const inner = (
    <div className="flex flex-col gap-1 min-w-0">
      <div className="flex items-center gap-1.5 text-gray-500 dark:text-gray-400">
        <span className="flex-shrink-0">{icon}</span>
        <span className="font-inter-tight text-[10px] font-semibold uppercase tracking-wider">{label}</span>
      </div>
      <div className="font-inter-tight text-sm font-medium text-gray-900 dark:text-white truncate flex items-center gap-1">
        {value}
        {href && <FiExternalLink className="w-3 h-3 flex-shrink-0 text-gray-400 dark:text-gray-500" />}
      </div>
    </div>
  );

  const wrapperClass =
    "block rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] bg-gray-50 dark:bg-[#0f0f12] p-3 transition-colors";

  if (href) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer nofollow"
        onClick={(e) => e.stopPropagation()}
        className={`${wrapperClass} hover:bg-white dark:hover:bg-[#18181D] hover:border-gray-300 dark:hover:border-[#25252B]`}
      >
        {inner}
      </a>
    );
  }
  return <div className={wrapperClass}>{inner}</div>;
}

export default function ResolutionDetails({
  source,
  eventData,
  selectedMarketDetails,
  selectedMarket,
}: ResolutionDetailsProps) {
  const t = useT();
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);
  // Mirrors the FAQ card pattern in EventClient — the heading icon
  // animates on card hover so this section reads as a peer of FAQ /
  // Market Talks instead of a plain text panel.
  const [headerHovered, setHeaderHovered] = useState(false);

  const mkt = selectedMarketDetails?.market;
  const desc = mkt?.description || eventData.description;
  if (!desc && !mkt?.resolutionSource) return null;

  const paragraphs = desc?.split('\n\n').filter((p: string) => p.trim()) || [];
  const firstParagraph = paragraphs[0] || '';
  const hasMore = paragraphs.length > 1 || !!mkt?.resolutionSource || source === 'kalshi';

  /** Render text with Markdown-style formatting: [text](url), **bold**, raw URLs, and - lists */
  const renderMarkdown = (text: string) => {
    const lines = text.split('\n');
    const elements: React.ReactNode[] = [];

    let listItems: string[] = [];

    const flushList = () => {
      if (listItems.length === 0) return;
      elements.push(
        <ul key={`list-${elements.length}`} className="list-disc list-inside space-y-1 my-1">
          {listItems.map((item, i) => (
            <li key={i} className="font-inter-tight text-sm text-gray-600 dark:text-gray-300">{renderInline(item)}</li>
          ))}
        </ul>
      );
      listItems = [];
    };

    const linkClass =
      "text-gray-900 dark:text-white underline decoration-gray-300 dark:decoration-gray-600 hover:decoration-gray-900 dark:hover:decoration-white underline-offset-2 transition-colors";

    const renderInline = (line: string): React.ReactNode[] => {
      const tokens: React.ReactNode[] = [];
      const regex = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)|\*\*([^*]+)\*\*|(https?:\/\/[^\s)]+)/g;
      let lastIndex = 0;
      let match;

      while ((match = regex.exec(line)) !== null) {
        if (match.index > lastIndex) {
          tokens.push(line.slice(lastIndex, match.index));
        }

        if (match[1] && match[2]) {
          tokens.push(
            <a key={`${match.index}`} href={match[2]} target="_blank" rel="noopener noreferrer nofollow" onClick={(e) => e.stopPropagation()} className={linkClass}>
              {match[1]}
            </a>
          );
        } else if (match[3]) {
          tokens.push(<strong key={`${match.index}`} className="font-semibold text-gray-900 dark:text-white">{match[3]}</strong>);
        } else if (match[4]) {
          // Raw URL: show domain for readability, full URL on hover/click.
          tokens.push(
            <a
              key={`${match.index}`}
              href={match[4]}
              target="_blank"
              rel="noopener noreferrer nofollow"
              onClick={(e) => e.stopPropagation()}
              title={match[4]}
              className={linkClass}
            >
              {extractDomain(match[4])}
            </a>
          );
        }

        lastIndex = match.index + match[0].length;
      }

      if (lastIndex < line.length) {
        tokens.push(line.slice(lastIndex));
      }

      return tokens.length > 0 ? tokens : [line];
    };

    for (const line of lines) {
      const trimmed = line.trim();
      if (trimmed.startsWith('- ')) {
        listItems.push(trimmed.slice(2));
      } else {
        flushList();
        if (trimmed) {
          elements.push(<span key={`p-${elements.length}`}>{renderInline(trimmed)} </span>);
        }
      }
    }
    flushList();

    return elements;
  };

  const formatDate = (d: string) =>
    new Date(d).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZone: 'America/New_York',
    }) + ' ET';

  const formatShortDate = (d: string) =>
    new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  const payoutLabel = (() => {
    const secs = mkt?.settlementTimerSeconds;
    if (!secs || secs <= 0) return t("resolution.payout.aboutMinute");
    if (secs < 60) return t("resolution.payout.aboutMinute");
    if (secs < 3600) {
      const m = Math.round(secs / 60);
      return t(m === 1 ? "resolution.payout.minute" : "resolution.payout.minutes", { n: m });
    }
    const hrs = Math.round(secs / 3600);
    return t(hrs === 1 ? "resolution.payout.hour" : "resolution.payout.hours", { n: hrs });
  })();

  return (
    <div
      className={`bg-white dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] p-4 md:p-6 transition-colors ${hasMore ? 'cursor-pointer' : ''}`}
      onClick={hasMore ? () => setDescriptionExpanded(!descriptionExpanded) : undefined}
      onMouseEnter={() => setHeaderHovered(true)}
      onMouseLeave={() => setHeaderHovered(false)}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <GavelIcon size={18} className="text-gray-400 dark:text-gray-500" isHovered={headerHovered} />
          <h3 className="font-inter-tight text-base md:text-[17px] font-semibold text-gray-900 dark:text-white">
            {source === 'kalshi' ? t("resolution.title.market") : t("resolution.title.event")}
          </h3>
        </div>
        {hasMore && (
          <div className={`transition-transform duration-200 ${descriptionExpanded ? 'rotate-180' : ''}`}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-400 dark:text-gray-500"><path d="m6 9 6 6 6-6"/></svg>
          </div>
        )}
      </div>

      {/* Metadata grid */}
      {(mkt?.createdAt || mkt?.resolvedBy || mkt?.resolutionSource) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 mb-4">
          {mkt?.createdAt && (
            <MetaCard
              icon={<FiCalendar className="w-3.5 h-3.5" />}
              label={t("resolution.meta.created")}
              value={<span className="font-inter-tight text-sm font-medium text-gray-900 dark:text-white">{formatDate(mkt.createdAt)}</span>}
            />
          )}
          {mkt?.resolvedBy && (
            <MetaCard
              icon={<FiUser className="w-3.5 h-3.5" />}
              label={t("resolution.meta.resolver")}
              href={`https://polygonscan.com/address/${mkt.resolvedBy}`}
              value={<span className="font-inter-tight text-sm font-medium text-gray-900 dark:text-white tabular-nums">{mkt.resolvedBy.slice(0, 6)}…{mkt.resolvedBy.slice(-4)}</span>}
            />
          )}
          {mkt?.resolutionSource && (
            <MetaCard
              icon={<FiLink2 className="w-3.5 h-3.5" />}
              label={t("resolution.meta.source")}
              href={mkt.resolutionSource}
              value={<span className="font-inter-tight text-sm font-medium text-gray-900 dark:text-white truncate">{extractDomain(mkt.resolutionSource)}</span>}
            />
          )}
        </div>
      )}

      {/* Description panel */}
      {firstParagraph && (
        <div className="rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] bg-gray-50 dark:bg-[#0f0f12] p-4">
          <p className="font-inter-tight text-sm text-gray-700 dark:text-gray-300 leading-relaxed">{renderMarkdown(firstParagraph)}</p>
          {descriptionExpanded && paragraphs.length > 1 && (
            <div className="mt-3 space-y-3">
              {paragraphs.slice(1).map((p: string, i: number) => (
                <p key={i} className="font-inter-tight text-sm text-gray-700 dark:text-gray-300 leading-relaxed">{renderMarkdown(p)}</p>
              ))}
            </div>
          )}
          {/* Inline expand affordance — the header chevron lives top-right
              and reads as "section toggle", not "this paragraph is
              truncated". Surface a visible "Show more / Show less" with
              its own chevron right under the description so the user can
              tell at a glance that there's collapsed content below. */}
          {hasMore && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setDescriptionExpanded(!descriptionExpanded); }}
              className="mt-2 inline-flex items-center gap-1 font-inter-tight text-xs font-medium text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
            >
              {descriptionExpanded ? t("resolution.expand.showLess") : t("resolution.expand.showMore")}
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.25"
                strokeLinecap="round"
                strokeLinejoin="round"
                className={`transition-transform duration-200 ${descriptionExpanded ? 'rotate-180' : ''}`}
              >
                <path d="m6 9 6 6 6-6" />
              </svg>
            </button>
          )}
        </div>
      )}

      {/* Kalshi: Enriched rules, timeline, settlement sources, identifiers */}
      {descriptionExpanded && source === 'kalshi' && mkt && (
        <div className="mt-4 space-y-4" onClick={(e) => e.stopPropagation()}>
          {/* Secondary rules */}
          {mkt.rulesSecondary && (
            <div className="rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] bg-gray-50 dark:bg-[#0f0f12] p-4">
              <p className="font-inter-tight text-sm text-gray-700 dark:text-gray-300 leading-relaxed">{mkt.rulesSecondary}</p>
            </div>
          )}

          {/* Timeline cards */}
          <div>
            <h4 className="font-inter-tight text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
              {t("resolution.section.timeline")}
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {mkt.createdAt && (
                <MetaCard
                  icon={<FiCalendar className="w-3.5 h-3.5" />}
                  label={t("resolution.meta.marketOpen")}
                  value={<span className="font-inter-tight text-sm font-medium text-gray-900 dark:text-white">{formatShortDate(mkt.createdAt)}</span>}
                />
              )}
              {mkt.endDate && (
                <MetaCard
                  icon={<FiClock className="w-3.5 h-3.5" />}
                  label={t("resolution.meta.marketCloses")}
                  value={
                    <span className="font-inter-tight text-sm font-medium text-gray-900 dark:text-white">
                      {formatShortDate(mkt.endDate)}
                      {mkt.canCloseEarly && mkt.earlyCloseCondition && (
                        <span className="block text-[11px] font-normal text-gray-500 dark:text-gray-400">{t("resolution.meta.mayCloseEarly")}</span>
                      )}
                    </span>
                  }
                />
              )}
              <MetaCard
                icon={<FiShield className="w-3.5 h-3.5" />}
                label={t("resolution.meta.payout")}
                value={<span className="font-inter-tight text-sm font-medium text-gray-900 dark:text-white">{payoutLabel}</span>}
              />
            </div>
          </div>

          {/* Early close condition */}
          {mkt.canCloseEarly && mkt.earlyCloseCondition && (
            <div className="rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] bg-gray-50 dark:bg-[#0f0f12] p-3">
              <span className="font-inter-tight text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 block mb-1">
                {t("resolution.section.earlyClose")}
              </span>
              <p className="font-inter-tight text-sm text-gray-700 dark:text-gray-300 leading-relaxed">{mkt.earlyCloseCondition}</p>
            </div>
          )}

          {/* Mutually exclusive info */}
          {eventData?.mutuallyExclusive && eventData.markets && eventData.markets.length > 1 && (
            <div className="rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] bg-gray-50 dark:bg-[#0f0f12] p-3">
              <p className="font-inter-tight text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                {t("resolution.exclusive.text", {
                  collateral: eventData.collateralReturnType
                    ? t("resolution.exclusive.collateral", { type: eventData.collateralReturnType })
                    : "",
                })}
              </p>
            </div>
          )}

          {/* Settlement sources */}
          {eventData?.settlementSources && eventData.settlementSources.length > 0 && (
            <div>
              <h4 className="font-inter-tight text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                {t("resolution.section.outcomeVerified")}
              </h4>
              <div className="flex flex-wrap gap-2">
                {eventData.settlementSources.map((src, i) => (
                  <a
                    key={i}
                    href={src.url}
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] bg-gray-50 dark:bg-[#0f0f12] hover:bg-white dark:hover:bg-[#18181D] hover:border-gray-300 dark:hover:border-[#25252B] transition-colors font-inter-tight text-xs font-medium text-gray-900 dark:text-white"
                  >
                    {src.name}
                    <FiExternalLink className="w-3 h-3 text-gray-400 dark:text-gray-500" />
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Tickers */}
          {(mkt.eventTicker || selectedMarket?.slug) && (
            <div>
              <h4 className="font-inter-tight text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                {t("resolution.section.identifiers")}
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                {mkt.eventTicker && (
                  <>
                    <MetaCard
                      icon={<FiLink2 className="w-3.5 h-3.5" />}
                      label={t("resolution.meta.series")}
                      value={<span className="font-inter-tight text-sm font-medium text-gray-900 dark:text-white tabular-nums truncate">{mkt.eventTicker.includes('-') ? mkt.eventTicker.split('-').slice(0, -1).join('-') : mkt.eventTicker}</span>}
                    />
                    <MetaCard
                      icon={<FiLink2 className="w-3.5 h-3.5" />}
                      label={t("resolution.meta.event")}
                      value={<span className="font-inter-tight text-sm font-medium text-gray-900 dark:text-white tabular-nums truncate">{mkt.eventTicker}</span>}
                    />
                  </>
                )}
                {selectedMarket?.slug && (
                  <MetaCard
                    icon={<FiLink2 className="w-3.5 h-3.5" />}
                    label={t("resolution.meta.market")}
                    value={<span className="font-inter-tight text-sm font-medium text-gray-900 dark:text-white tabular-nums truncate">{selectedMarket.slug}</span>}
                  />
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
